"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResilientPipelineBuilder = void 0;
const guard_1 = require("./utils/guard");
const pipelineProxy_1 = require("./pipeline/pipelineProxy");
const noLogger_1 = require("./logging/noLogger");
const circuitBreakerState_1 = require("./resilience/circuitBreakerState");
const circuitBreakerProxy_1 = require("./resilience/circuitBreakerProxy");
const retryProxy_1 = require("./resilience/retryProxy");
const timeoutProxy_1 = require("./resilience/timeoutProxy");
const multiLogger_1 = require("./logging/multiLogger");
const consoleLogger_1 = require("./logging/consoleLogger");
const appInsightsLogger_1 = require("./logging/appInsightsLogger");
const baselineProxy_1 = require("./resilience/baselineProxy");
/**
 * Builder for a resilient pipeline.
 */
class ResilientPipelineBuilder {
    /**
     * Initializes a new instance of the @see ResiliencePipelineBuilder class.
     */
    constructor() {
        this.proxies = {};
        this.circuitBreakerIncluded = false;
        this.retryIncluded = false;
        this.timeoutIncluded = false;
        this.baselineIncluded = false;
        this.loggers = [];
    }
    /**
     * Gets a new builder.
     * @returns A new resilient pipleline builder.
     */
    static New() {
        return new ResilientPipelineBuilder();
    }
    /**
     * Prepares the loggers.
     * @returns The loggers.
     */
    static prepareLoggers(loggers) {
        let logger = null;
        switch (loggers.length) {
            case 0:
                logger = new noLogger_1.NoLogger();
                break;
            case 1:
                logger = loggers[0];
                break;
            default:
                logger = new multiLogger_1.MultiLogger(loggers);
                break;
        }
        return logger;
    }
    /**
     * Uses baseline measurement and log warning.
     * @param startSamplingAfter A timespan after when sampling should start. Use this if server needs a longer time to start.
     * @param maxSampleDuration A timespan within samples should be gathered after sampling start.
     * @param maxSamplesCount The maximum number of samples.
     * @returns The builder.
     */
    useBaseline(startSamplingAfter, maxSampleDuration, maxSamplesCount) {
        guard_1.Guard.throwIfNullOrNegative(startSamplingAfter, 'startSamplingAfter');
        guard_1.Guard.throwIfNullOrNegative(maxSampleDuration, 'maxSampleDuration');
        guard_1.Guard.throwIfNullOrNegative(maxSamplesCount, 'maxSamplesCount');
        this.baselineIncluded = true;
        this.baselineMaxSampleDuration = maxSampleDuration;
        this.baselineMaxSamplesCount = maxSamplesCount;
        this.baselineStartSamplingAfter = startSamplingAfter;
        return this;
    }
    /**
     * Adds a logger.
     * @param logger Logger to include.
     * @returns The builder.
     */
    useCustomLogger(logger) {
        guard_1.Guard.throwIfNullOrEmpty(logger, 'logger');
        this.loggers.push(logger);
        return this;
    }
    /**
     * Adds a console logger.
     * @param logLevel The minimum log level this logger accepts for log messages. If not set, LogLevel.Trace will be used.
     */
    useConsoleLogger(logLevel) {
        this.loggers.push(new consoleLogger_1.ConsoleLogger(logLevel));
        return this;
    }
    /**
     * Adds an Azure Application Insights logger.
     * @param client Application Insights client to use.
     * @param logLevel The minimum log level this logger accepts for log messages. If not set, LogLevel.Trace will be used.
     */
    useAppInsightsLogger(client, logLevel) {
        guard_1.Guard.throwIfNullOrEmpty(client, 'client');
        this.loggers.push(new appInsightsLogger_1.AppInsightsLogger(client, logLevel));
        return this;
    }
    /**
     * Adds a custom resilience proxy implementation.
     * @param position The position in the pipeline.
     * @param proxy Custom proxy to add.
     * @returns The builder.
     */
    addProxy(position, proxy) {
        guard_1.Guard.throwIfNullOrEmpty(proxy, 'proxy');
        if (!this.isPositionFree(position)) {
            throw new Error(`Position '${position}' is already in use in the pipeline!`);
        }
        this.proxies[position] = proxy;
        return this;
    }
    /**
     * Includes a circuit breaker proxy.
     * @param position Position of the circuit breaker in the pipeline.
     * @param breakDurationMs Minimum time in milli seconds circuit breaker will stay in open state.
     * @param maxFailedCalls Maximum failed calls before opening the circuit breaker.
     * @param leakTimeSpanInMilliSeconds Timespan in milliseconds in within errors are counted against max failed calls.
     * @param stateChangedCallback Callback to invoke if internal state has changed.
     * @param initialState The initial state of the circuit breaker.
     * @returns The builder.
     */
    useCircuitBreaker(position, breakDurationMs, maxFailedCalls, leakTimeSpanInMilliSeconds, stateChangedCallback, initialState = circuitBreakerState_1.CircuitBreakerState.Close) {
        guard_1.Guard.throwIfNullOrEmpty(breakDurationMs, 'breakDurationMs');
        guard_1.Guard.throwIfNullOrEmpty(maxFailedCalls, 'maxFailedCalls');
        guard_1.Guard.throwIfNullOrEmpty(leakTimeSpanInMilliSeconds, 'leakTimeSpanInMilliSeconds');
        if (!this.isPositionFree(position)) {
            throw new Error(`Position '${position}' is already in use in the pipeline!`);
        }
        this.circuitBreakerIncluded = true;
        this.circuitBreakerPosition = position;
        this.circuitBreakerBreakDurationMs = breakDurationMs;
        this.circuitBreakerMaxFailedCalls = maxFailedCalls;
        this.circuitBreakerStateChangedCallback = stateChangedCallback;
        this.circuitBreakerInitialState = initialState;
        this.circuitBreakerLeakTimeSpanInMilliSeconds = leakTimeSpanInMilliSeconds;
        return this;
    }
    /**
     * Includes a retry proxy.
     * @param position Position in the pipeline.
     * @param retries Number of retries to execute.
     */
    useRetry(position, retries) {
        guard_1.Guard.throwIfNullOrEmpty(retries, 'retries');
        if (!this.isPositionFree(position)) {
            throw new Error(`Position '${position}' is already in use in the pipeline!`);
        }
        this.retryIncluded = true;
        this.retryPosition = position;
        this.retryRetries = retries;
        return this;
    }
    /**
     * Includes a timeout into the pipeline.
     * @param position Position in the pipeline.
     * @param timeoutMs Timeout in MS within function must succeed or else a timeout error is thrown.
     * @returns The builder.
     */
    useTimeout(position, timeoutMs) {
        guard_1.Guard.throwIfNullOrEmpty(timeoutMs, 'timeoutMs');
        if (!this.isPositionFree(position)) {
            throw new Error(`Position '${position}' is already in use in the pipeline!`);
        }
        this.timeoutIncluded = true;
        this.timeoutPosition = position;
        this.timeoutTimeoutMs = timeoutMs;
        return this;
    }
    /**
     * Builds to list of proxies.
     * @returns List of proxies.
     */
    buildToList() {
        const logger = ResilientPipelineBuilder.prepareLoggers(this.loggers);
        if (this.circuitBreakerIncluded) {
            this.proxies[this.circuitBreakerPosition] = new circuitBreakerProxy_1.CircuitBreakerProxy(this.circuitBreakerBreakDurationMs, this.circuitBreakerMaxFailedCalls, this.circuitBreakerLeakTimeSpanInMilliSeconds, logger, this.circuitBreakerStateChangedCallback, this.circuitBreakerInitialState);
        }
        if (this.retryIncluded) {
            this.proxies[this.retryPosition] = new retryProxy_1.RetryProxy(this.retryRetries, logger);
        }
        if (this.timeoutIncluded) {
            this.proxies[this.timeoutPosition] = new timeoutProxy_1.TimeoutProxy(this.timeoutTimeoutMs, logger);
        }
        const keys = [];
        for (const key in this.proxies) {
            if (this.proxies.hasOwnProperty(key)) {
                keys.push(Number.parseInt(key, 10));
            }
        }
        const sortedKeys = keys.sort((n1, n2) => n1 - n2);
        const sortedProxies = [];
        for (const key of sortedKeys) {
            sortedProxies.push(this.proxies[key]);
        }
        if (this.baselineIncluded) {
            const baseline = new baselineProxy_1.BaselineProxy(this.baselineStartSamplingAfter, this.baselineMaxSampleDuration, this.baselineMaxSamplesCount, logger);
            sortedProxies.push(baseline);
        }
        return sortedProxies;
    }
    /**
     * Builds the resilience pipeline.
     * @returns The resilience pipeline.
     */
    build() {
        const sortedProxies = this.buildToList();
        return new pipelineProxy_1.PipelineProxy(sortedProxies);
    }
    /**
     * Gets a value indicating whether a position in the pipeline is free.
     * @param position Position to check.
     * @returns True if the position is free, else false.
     */
    isPositionFree(position) {
        if (this.proxies[position]) {
            return false;
        }
        if (this.circuitBreakerIncluded &&
            this.circuitBreakerPosition === position) {
            return false;
        }
        if (this.retryIncluded && this.retryPosition === position) {
            return false;
        }
        if (this.timeoutIncluded && this.timeoutPosition === position) {
            return false;
        }
        return true;
    }
}
exports.ResilientPipelineBuilder = ResilientPipelineBuilder;
//# sourceMappingURL=resilientPipelineBuilder.js.map